import { defineStore } from 'pinia'
import { cartService } from '@/services/api/cartService'
import { orderService } from '@/services/api/orderService'

const n = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; }

const calcItemTotal = (item) => {
  let t = n(item.price) * (item.quantity || 1);
  if (item.selectedExtras?.length) {
    t += item.selectedExtras.reduce((s, ex) => {
      return s + n(ex.priceModifier ?? ex.price) * (ex.quantity || 1);
    }, 0) * (item.quantity || 1);
  }
  return t;
}

function getOrCreateGuestId() {
  let guestId = localStorage.getItem('guestId')
  if (!guestId) {
    guestId = 'g_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    localStorage.setItem('guestId', guestId)
  }
  return guestId
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    items: [],
    tableId: null,
    sessionUniqueId: null,
    sessionNumericId: null,
    customerNote: '',
    loading: false,
    error: null,
    _lastUsedCartKey: null
  }),
  
  getters: {
    count: (state) => state.items.length,
    isEmpty: (state) => state.items.length === 0,
    totalItems: (state) => state.items.reduce((total, item) => total + item.quantity, 0),
    
    subtotal: (state) => {
      return state.items
        .filter(item => !item.isDiscount)
        .reduce((sum, item) => sum + calcItemTotal(item), 0);
    },

    activeDiscountPercent: (state) => {
      return Math.min(
        state.items
          .filter(item => item.isDiscount && item.discountPercent > 0)
          .reduce((sum, item) => sum + item.discountPercent, 0),
        100
      );
    },

    discountAmount() {
      return this.subtotal * this.activeDiscountPercent / 100;
    },
    
    tax() {
      const taxRate = 0.19;
      const afterDiscount = Math.max(0, this.subtotal - this.discountAmount);
      const net = afterDiscount / (1 + taxRate);
      return afterDiscount - net;
    },
    
    total() {
      return Math.max(0, this.subtotal - this.discountAmount);
    },
    
    // Liefergebühr (0 für "Am Tisch essen" oder Selbstabholung)
    deliveryFee: () => 0,

    serviceCharge: () => 0,
    
    // Alias für customerNote (Kompatibilität)
    overallSpecialInstructions: (state) => state.customerNote,
    
    // Getter zum Finden eines Items anhand der uniqueItemId
    getItemByUniqueId: (state) => (uniqueId) => {
      return state.items.find(item => item.uniqueItemId === uniqueId);
    }
  },
  
  actions: {
    // Session-Informationen setzen
    setSession(tableId, sessionNumericId, sessionUniqueId) {
      this.tableId = tableId
      this.sessionNumericId = sessionNumericId
      this.sessionUniqueId = sessionUniqueId
      
      // Warenkorb aus dem localStorage laden, falls vorhanden - ENTFERNT
      // this.loadFromStorage()
      
      // Warenkorb mit dem Server synchronisieren - ENTFERNT
      // this.syncWithServer()
    },
    
    // Synchronisiert den lokalen Warenkorb mit dem Server
    async syncWithServer() {
      if (!this.sessionNumericId || !this.sessionUniqueId) return
      
      try {
        // getCart erwartet die numerische ID
        const response = await cartService.getCart(this.sessionNumericId)
        
        // Server-Daten verarbeiten...
        if (response.data && response.data.items) {
           // TODO: Bessere Logik zur Synchronisierung/Zusammenführung?
           // Aktuell: Wenn Server Items hat und lokal leer ist -> Server gewinnt
          if (response.data.items.length > 0 && this.items.length === 0) {
              this.items = response.data.items.map(item => ({ // Map server items to local structure if needed
                  id: item.productId, // Assuming server returns productId
                  name: item.productName, // Assuming server returns productName
                  price: item.price,
                  quantity: item.quantity,
                  // Passe die Extra-Struktur an, erwarte priceModifier oder price vom Server
                  selectedExtras: (item.extraOptions || []).map(extra => ({
                    ...extra, // Übernehme alle Felder vom Server
                    // Stelle sicher, dass wir das Preisfeld haben, das die Getter erwarten
                    priceModifier: extra.priceModifier !== undefined ? extra.priceModifier : extra.price
                  })),
                  specialInstructions: item.specialInstructions || '',
                  serverItemId: item.id, // Store the server's item ID
                  uniqueItemId: item.uniqueItemId || (Date.now() + '_' + Math.random().toString(36).substr(2, 9)) // Add uniqueItemId if not present
              }));
              this.customerNote = response.data.customerNote || ''
              this.saveToStorage()
          } else if (this.items.length > 0) {
             // TODO: Implementiere Upload des lokalen Warenkorbs zum Server?
             // Der cartService hat keine direkte 'saveCart' Funktion mehr.
             // Stattdessen müssten die Items einzeln mit addToCart hinzugefügt werden,
             // was komplex ist. Vorerst auskommentiert.
             /*
             console.warn('Server-Upload-Logik in syncWithServer nicht implementiert.');
             await cartService.saveCart({
               sessionId: this.sessionNumericId, // braucht numerische ID?
               items: this.items,
               customerNote: this.customerNote
             })
             */
          }
        }

      } catch (error) {
        console.error('Fehler bei der Synchronisierung des Warenkorbs:', error)
      }
    },
    
    // Produkt zum Warenkorb hinzufügen
    async addItem(product, quantity = 1, selectedExtras = [], specialInstructions = '') {
      console.log('Adding to cart:', {
        product,
        quantity,
        extras: selectedExtras,
        instructions: specialInstructions
      });
      
      const extras = Array.isArray(selectedExtras) ? selectedExtras : [];
      
      // Lokale Logik zum Finden/Hinzufügen/Aktualisieren...
      const existingItemIndex = this.items.findIndex(item => {
        if (item.id !== product.id) return false;
        if (item.specialInstructions !== specialInstructions) return false;
        
        // Prüfen, ob die ausgewählten Extras identisch sind
        if (!item.selectedExtras || !Array.isArray(item.selectedExtras)) return false;
        if (item.selectedExtras.length !== extras.length) return false;
        
        return item.selectedExtras.every(extra => {
          return extras.some(newExtra => newExtra.id === extra.id);
        });
      });
      
      if (existingItemIndex !== -1) {
        // Artikel existiert bereits, Menge erhöhen
        this.items[existingItemIndex].quantity += quantity;
      } else {
        // Neuen Artikel hinzufügen
        const newItem = {
          id: product.id,
          name: product.name,
          price: n(product.price),
          description: product.description || '',
          quantity,
          selectedExtras: extras.map(ex => ({
            ...ex,
            price: n(ex.price ?? ex.priceModifier),
            priceModifier: n(ex.priceModifier ?? ex.price)
          })),
          specialInstructions,
          image: product.image || null,
          imageUrl: product.imageUrl || null,
          serverItemId: null,
          uniqueItemId: Date.now() + '_' + Math.random().toString(36).substr(2, 9),
          isDiscount: !!product.isDiscount,
          discountPercent: n(product.discountPercent),
          isFreeReward: !!product.isFreeReward,
        };
        
        this.items.push(newItem);
      }
      
      // Lokal speichern (immer)
      this.saveToStorage();
      
      // Virtuelle Rabatt-Items nicht an den Server senden (echte Gratis-Produkte schon)
      if (this.sessionNumericId && !product.isDiscount) {
        try {
          const itemDto = { // OrderItemCreateDto Format
            productId: product.id,
            quantity: quantity,
            specialInstructions: specialInstructions || '',
            extraOptionIds: extras.map(extra => extra.id) 
          };
          // Verwende cartService.addToCart mit numerischer ID
          const response = await cartService.addToCart(this.sessionNumericId, itemDto);
          
          // Aktualisiere ggf. lokale Items mit Daten vom Server
          console.log('Item zum Server hinzugefügt, Antwort:', response.data);
        } catch (error) {
          console.error('Fehler beim Hinzufügen des Artikels zum Server:', error);
          // Fehler nicht weitergeben, da der lokale Warenkorb trotzdem funktionieren soll
        }
      } else {
        console.warn('Item nur lokal gespeichert (kein sessionNumericId vorhanden)');
      }
    },
    
    // Artikel aus dem Warenkorb entfernen
    async removeItem(index) {
      if (index < 0 || index >= this.items.length) return;
      
      const itemToRemove = this.items[index];
      this.items.splice(index, 1);
      this.saveToStorage();
      
      // Vom Server entfernen (nur wenn sessionNumericId vorhanden ist)
      if (this.sessionNumericId && itemToRemove.serverItemId) {
        try {
          await cartService.removeFromCart(this.sessionNumericId, itemToRemove.serverItemId);
        } catch (error) {
          console.error('Fehler beim Entfernen des Artikels auf dem Server:', error);
          // Fehler nicht weitergeben, da der lokale Warenkorb trotzdem funktionieren soll
        }
      } else if (!this.sessionNumericId) {
        console.warn('Item nur lokal entfernt (kein sessionNumericId vorhanden)');
      } else if (!itemToRemove.serverItemId) {
        console.warn('Item ohne serverItemId kann nicht vom Server entfernt werden');
      }
    },
    
    // Artikelmenge aktualisieren
    async updateQuantity(index, quantity) {
      if (quantity < 1 || index < 0 || index >= this.items.length) return;
      
      const itemToUpdate = this.items[index];
      itemToUpdate.quantity = quantity;
      this.saveToStorage();
      
      // Menge auf Server aktualisieren (nur wenn sessionNumericId vorhanden ist)
      if (this.sessionNumericId && itemToUpdate.serverItemId) {
        try {
          await cartService.updateQuantity(
            this.sessionNumericId, 
            itemToUpdate.serverItemId, 
            quantity
          );
        } catch (error) {
          console.error('Fehler beim Aktualisieren der Menge auf dem Server:', error);
          // Fehler nicht weitergeben, da der lokale Warenkorb trotzdem funktionieren soll
        }
      } else if (!this.sessionNumericId) {
        console.warn('Menge nur lokal aktualisiert (kein sessionNumericId vorhanden)');
      } else if (!itemToUpdate.serverItemId) {
        console.warn('Item ohne serverItemId kann nicht auf dem Server aktualisiert werden');
      }
    },
    
    // Menge erhöhen
    increaseQuantity(index) {
      if (index < 0 || index >= this.items.length) return;
      this.updateQuantity(index, this.items[index].quantity + 1);
    },

    // Menge verringern
    decreaseQuantity(index) {
      if (index < 0 || index >= this.items.length) return;
      const item = this.items[index];
      if (item.quantity > 1) {
        this.updateQuantity(index, item.quantity - 1);
      } else {
        this.removeItem(index);
      }
    },

    // Spezielle Anweisungen für ein Item aktualisieren
    updateItemInstructions(index, instructions) {
      if (index < 0 || index >= this.items.length) return;
      this.items[index].specialInstructions = instructions;
      this.saveToStorage();
    },

    // Item anhand der uniqueItemId aktualisieren
    async updateItem(uniqueItemId, product, quantity, selectedExtras = [], specialInstructions = '') {
      const itemIndex = this.items.findIndex(item => item.uniqueItemId === uniqueItemId);
      if (itemIndex === -1) {
        console.error(`Item mit uniqueItemId ${uniqueItemId} nicht gefunden`);
        return;
      }

      const extras = Array.isArray(selectedExtras) ? selectedExtras : [];
      const itemToUpdate = this.items[itemIndex];
      
      // Aktualisiere das Item lokal
      itemToUpdate.quantity = quantity;
      itemToUpdate.selectedExtras = extras.map(ex => ({
        ...ex,
        price: n(ex.price ?? ex.priceModifier),
        priceModifier: n(ex.priceModifier ?? ex.price)
      }));
      itemToUpdate.specialInstructions = specialInstructions;
      
      // Lokal speichern
      this.saveToStorage();
      
      // Auf Server aktualisieren (nur wenn sessionNumericId vorhanden)
      if (this.sessionNumericId && itemToUpdate.serverItemId) {
        try {
          // Update quantity on server
          await cartService.updateQuantity(
            this.sessionNumericId,
            itemToUpdate.serverItemId,
            quantity
          );
          
          // Note: Full item update (extras, specialInstructions) would require backend support
          // For now, we update locally and quantity on server
          // If full server update is needed, we'd need to remove and re-add the item
          console.log('Item auf Server aktualisiert (Menge)');
        } catch (error) {
          console.error('Fehler beim Aktualisieren des Artikels auf dem Server:', error);
          // Fehler nicht weitergeben, da der lokale Warenkorb trotzdem funktionieren soll
        }
      } else if (!this.sessionNumericId) {
        console.warn('Item nur lokal aktualisiert (kein sessionNumericId vorhanden)');
      } else if (!itemToUpdate.serverItemId) {
        console.warn('Item ohne serverItemId kann nicht auf dem Server aktualisiert werden');
      }
    },

    // Warenkorbnotiz aktualisieren
    updateNote(note) {
      this.customerNote = note
      this.saveToStorage()
    },
    
    // Warenkorb leeren
    async clearCart() {
      this.items = []
      this.customerNote = ''
      this.saveToStorage()
      
      // Server-Warenkorb leeren (verwendet numerische ID)
      if (this.sessionNumericId) {
        try {
          await cartService.clearCart(this.sessionNumericId)
        } catch (error) {
          console.error('Fehler beim Leeren des Warenkorbs auf dem Server:', error)
           // TODO: Fehlerbehandlung
        }
      }
    },
    
    // Warenkorb aus dem localStorage laden
    loadFromStorage() {
      // Lese auch die Session-IDs aus dem localStorage
      const storedNumericId = localStorage.getItem('sessionNumericId');
      const storedUniqueId = localStorage.getItem('sessionUniqueId');
      const storedTableId = localStorage.getItem('tableId'); // Tisch-ID auch laden

      // Setze die IDs im Store, falls im localStorage vorhanden und im Store noch nicht gesetzt
      // Wichtig: Nur setzen, wenn sie im aktuellen Store-State noch fehlen, 
      // um nicht versehentlich eine aktive, anders gesetzte Session zu überschreiben.
      if (storedNumericId && !this.sessionNumericId) {
        this.sessionNumericId = parseInt(storedNumericId, 10);
      }
      if (storedUniqueId && !this.sessionUniqueId) {
        this.sessionUniqueId = storedUniqueId;
      }
      if (storedTableId && !this.tableId) { // tableId auch wiederherstellen
         this.tableId = parseInt(storedTableId, 10) || null; // In Zahl umwandeln oder null
      }

      const guestId = getOrCreateGuestId()
      const cartKey = `cart_${this.sessionUniqueId || 'default'}_${this.tableId || 'default'}_${guestId}`
      this._lastUsedCartKey = cartKey; // Merke dir den Key für saveToStorage
      const savedCart = localStorage.getItem(cartKey)
      
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart)
          this.items = parsedCart.items || []
          this.customerNote = parsedCart.customerNote || ''
          console.log('Cart loaded from storage for key:', cartKey, this.items);
        } catch (error) {
          console.error('Fehler beim Laden des Warenkorbs aus localStorage:', error)
          this.items = [];
          this.customerNote = '';
        }
      } else {
        // Wenn kein Warenkorb für diesen Key existiert, State leeren
        this.items = [];
        this.customerNote = '';
         console.log('No cart found in storage for key:', cartKey);
      }
    },
    
    // Warenkorb im localStorage speichern
    saveToStorage() {
      const guestId = getOrCreateGuestId()
      const cartKey = this._lastUsedCartKey || 
        `cart_${this.sessionUniqueId || 'default'}_${this.tableId || 'default'}_${guestId}`
        
      const cartData = {
        items: this.items,
        customerNote: this.customerNote,
        timestamp: new Date().getTime()
      }
      
      localStorage.setItem(cartKey, JSON.stringify(cartData))
      this._lastUsedCartKey = cartKey
    },
    
    // Bestellung aufgeben
    async checkout() {
      if (this.isEmpty) {
        this.error = 'Der Warenkorb ist leer'
        return { success: false, error: this.error }
      }
      
      // Stelle sicher, dass BEIDE IDs vorhanden sind
      if (!this.sessionNumericId || !this.sessionUniqueId) {
         this.error = 'Session nicht korrekt initialisiert. Numerische ID oder GUID fehlt.'
         console.error(this.error, `Numeric: ${this.sessionNumericId}, Unique: ${this.sessionUniqueId}`);
         return { success: false, error: this.error }
      }

      this.loading = true
      this.error = null
      
      try {
        const orderData = { 
          tableId: this.tableId,
          sessionId: this.sessionNumericId, 
          sessionUniqueId: this.sessionUniqueId, 
          items: this.items
            .filter(item => !item.isDiscount && !item.isFreeReward)
            .map(item => ({
              productId: item.id,
              quantity: item.quantity,
              specialInstructions: item.specialInstructions || '',
              extraOptionIds: item.selectedExtras?.map(extra => extra.id) || [] 
            })),
          customerNote: this.customerNote || '',
          discountPercent: this.activeDiscountPercent || 0,
          discountAmount: this.discountAmount || 0,
        }
        
        console.log('Sende Bestelldaten (checkout):', orderData);

        // Annahme: orderService.placeOrder ruft den Backend-Endpunkt auf, der CreateOrderAsync triggert
        const response = await orderService.placeOrder(orderData) 
        
        this.clearCart() // Lokalen und Server-Warenkorb leeren
        
        return {
          success: true,
          data: response.data
        }
      } catch (error) {
        console.error('Fehler beim Aufgeben der Bestellung:', error)
        this.error = error.response?.data?.message || 
          'Beim Aufgeben der Bestellung ist ein Fehler aufgetreten.'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    }
  }
}) 